// src/pages/resident/ResidentPayments.jsx
import React, { useEffect, useState, useCallback } from "react";
import { getResidentCharges, createPaymentOrder, verifyPayment } from "../../services/billingAPI";
import html2pdf from "html2pdf.js";
import "./ResidentPayments.css";

export default function ResidentPayments() {
  const [charges, setCharges] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("pending"); // pending or history
  const [processingPayment, setProcessingPayment] = useState(false);

  // residentToken never has a companion "residentId" key in localStorage
  // (see ResidentLogin.jsx) — the id is only available inside the JWT
  // payload, so decode it the same way ResidentTheatre.jsx does.
  const token = localStorage.getItem("residentToken");
  const residentId = JSON.parse(
    atob((token || "").split(".")[1] || "e30=") || "{}"
  ).id;
  const residentName = localStorage.getItem("residentName");

  // Load charges on component mount
  const loadCharges = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (!residentId) {
        setError("Resident ID not found. Please log in again.");
        setLoading(false);
        return;
      }

      const data = await getResidentCharges(residentId);

      if (data.charges) {
        // Separate paid and pending charges
        const pending = data.charges.filter(c => c.paymentStatus === "unpaid");
        const paid = data.charges.filter(c => c.paymentStatus === "paid");

        setCharges(pending);
        setPaymentHistory(paid);
      }

      setLoading(false);
    } catch (err) {
      setError("Failed to load charges: " + err.message);
      setLoading(false);
    }
  }, [residentId]);

  useEffect(() => {
    loadCharges();
  }, [loadCharges]);


  const handlePayNow = async (charge) => {
    try {
      setProcessingPayment(true);
      setError("");

      // Step 1: Create Razorpay order
      const orderResponse = await createPaymentOrder(charge.id, residentId);

      if (!orderResponse.order) {
        throw new Error("Failed to create payment order");
      }

      const { orderId, amount, keyId } = orderResponse.order;

      // Step 2: Open Razorpay payment modal
      const options = {
        key: keyId,
        amount: amount * 100, // Convert to paise
        currency: "INR",
        name: "Crown Residences",
        description: `Maintenance Charges - ${charge.billingPeriodName}`,
        order_id: orderId,
        handler: async (response) => {
          try {
            // Step 3: Verify payment on backend
            const verifyResponse = await verifyPayment(
              orderId,
              response.razorpay_payment_id,
              response.razorpay_signature,
              charge.id,
              residentId
            );

            if (verifyResponse.verified) {
              setSuccess(
                `Payment of ₹${amount} received successfully! Invoice has been sent to your email.`
              );

              // Reload charges to reflect payment
              setTimeout(() => {
                loadCharges();
                setSuccess("");
              }, 3000);
            } else {
              setError("Payment verification failed. Please contact support.");
            }
          } catch (error) {
            setError("Payment verification failed: " + error.message);
          }
        },
        prefill: {
          name: residentName,
          email: localStorage.getItem("residentEmail"),
        },
        theme: {
          color: "#a9822f", // Your brand color
        },
      };

      // Load Razorpay script
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      };
      document.body.appendChild(script);

      setProcessingPayment(false);
    } catch (err) {
      setError("Payment failed: " + err.message);
      setProcessingPayment(false);
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(numAmount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const downloadInvoice = (charge) => {
    const invoiceHTML = `
      <div style="font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto;">
        <div style="border-bottom: 2px solid #a9822f; padding-bottom: 20px; margin-bottom: 30px;">
          <h1 style="color: #333; margin: 0; font-size: 28px;">Crown Residences</h1>
          <p style="color: #666; margin: 5px 0; font-size: 14px;">Premium Residential Community</p>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 40px;">
          <div>
            <h3 style="color: #a9822f; margin-bottom: 10px; font-size: 12px; text-transform: uppercase;">Bill To</h3>
            <p style="margin: 5px 0; font-size: 14px;"><strong>${residentName}</strong></p>
            <p style="margin: 5px 0; font-size: 14px;">${charge.tower || "Tower"}</p>
            <p style="margin: 5px 0; font-size: 14px;">Floor ${charge.floor}, Unit ${charge.unit}</p>
            <p style="margin: 5px 0; font-size: 14px;">Area: ${charge.usedArea} sqft</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 5px 0; font-size: 14px;"><strong>Invoice #:</strong> ${charge.id}</p>
            <p style="margin: 5px 0; font-size: 14px;"><strong>Period:</strong> ${charge.billingPeriodName}</p>
            <p style="margin: 5px 0; font-size: 14px;"><strong>Due Date:</strong> ${formatDate(charge.dueDate)}</p>
            <p style="margin: 5px 0; font-size: 14px; color: ${charge.paymentStatus === 'paid' ? '#27ae60' : '#e74c3c'};"><strong>${charge.paymentStatus.toUpperCase()}</strong></p>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <thead>
            <tr style="background-color: #f5f5f5; border-bottom: 2px solid #ddd;">
              <th style="padding: 12px; text-align: left; font-weight: bold; font-size: 13px;">Description</th>
              <th style="padding: 12px; text-align: center; font-weight: bold; font-size: 13px;">Rate</th>
              <th style="padding: 12px; text-align: right; font-weight: bold; font-size: 13px;">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid #eee;">
              <td style="padding: 12px; font-size: 13px;">Maintenance Charges</td>
              <td style="padding: 12px; text-align: center; font-size: 13px;">₹${parseFloat(charge.costPerSqft)}/sqft</td>
              <td style="padding: 12px; text-align: right; font-size: 13px;">${formatCurrency(charge.baseCost)}</td>
            </tr>
            ${charge.discountPercentage > 0 ? `
            <tr style="border-bottom: 1px solid #eee;">
              <td style="padding: 12px; font-size: 13px;">Discount (${charge.discountPercentage}%)</td>
              <td style="padding: 12px; text-align: center; font-size: 13px;">-</td>
              <td style="padding: 12px; text-align: right; font-size: 13px; color: #27ae60;">-${formatCurrency(charge.discountAmount)}</td>
            </tr>
            ` : ''}
          </tbody>
        </table>

        <div style="display: grid; grid-template-columns: 1fr 300px; gap: 30px; margin-bottom: 40px;">
          <div></div>
          <div style="background-color: #f9f9f9; padding: 20px; border-radius: 5px; border-left: 4px solid #a9822f;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px;">
              <strong>Total Due:</strong>
              <strong style="font-size: 18px; color: #a9822f;">${formatCurrency(charge.totalDue)}</strong>
            </div>
          </div>
        </div>

        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 5px; margin-bottom: 30px;">
          <h4 style="margin: 0 0 10px 0; color: #333; font-size: 13px;">Payment Instructions:</h4>
          <p style="margin: 5px 0; font-size: 12px; color: #666;">Pay online through the resident portal to avoid late charges. For queries, contact management@crownresidences.com</p>
        </div>

        <div style="text-align: center; border-top: 1px solid #ddd; padding-top: 20px; margin-top: 40px; color: #666; font-size: 11px;">
          <p>Crown Residences | Property Management</p>
          <p>This is a computer-generated invoice. No signature required.</p>
        </div>
      </div>
    `;

    const element = document.createElement("div");
    element.innerHTML = invoiceHTML;

    const options = {
      margin: 10,
      filename: `Invoice_${charge.id}_${new Date().toISOString().split("T")[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: "portrait", unit: "mm", format: "a4" },
    };

    html2pdf().set(options).from(element).save();
  };

  if (loading) {
    return (
      <div className="resident-content">
        <div className="resident-payments-container">
          <div className="loading">Loading charges...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="resident-content">
        <div className="resident-payments-container">
          <div className="payments-header">
            <h1>💰 Maintenance Charges</h1>
            <p>View and pay your maintenance charges online</p>
          </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-label">Pending Charges</div>
          <div className="card-value">
            <span style={{ fontSize: "16px", color: "#6b6558" }}>{charges.length} charge{charges.length !== 1 ? "s" : ""}</span>
            <span className="card-amount">
              {formatCurrency(charges.reduce((sum, c) => sum + c.totalDue, 0))}
            </span>
          </div>
        </div>

        <div className="summary-card paid">
          <div className="card-label">Paid Charges</div>
          <div className="card-value">
            <span style={{ fontSize: "16px", color: "#6b6558" }}>{paymentHistory.length} charge{paymentHistory.length !== 1 ? "s" : ""}</span>
            <span className="card-amount">
              {formatCurrency(paymentHistory.reduce((sum, c) => sum + c.totalDue, 0))}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          Pending ({charges.length})
        </button>
        <button
          className={`tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          Payment History ({paymentHistory.length})
        </button>
      </div>

      {/* Pending Charges */}
      {activeTab === "pending" && (
        <div className="charges-list">
          {charges.length === 0 ? (
            <div className="empty-state">
              <p>✅ No pending charges!</p>
              <p className="small">All your payments are up to date.</p>
            </div>
          ) : (
            charges.map((charge) => (
              <div key={charge.id} className="charge-card">
                <div className="charge-header">
                  <div className="charge-period">
                    <h3>{charge.billingPeriodName}</h3>
                    <span className="charge-frequency">{charge.billingFrequency}</span>
                  </div>
                  <div className="charge-amount">
                    <div className="amount">{formatCurrency(charge.totalDue)}</div>
                    <div className="status pending">PENDING</div>
                  </div>
                </div>

                <div className="charge-details">
                  <div className="detail-row">
                    <span className="label">Unit:</span>
                    <span className="value">Floor {charge.floor}, Unit {charge.unit}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Area:</span>
                    <span className="value">{charge.usedArea} sqft</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Rate:</span>
                    <span className="value">₹{parseFloat(charge.costPerSqft)}/sqft</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Base Cost:</span>
                    <span className="value">{formatCurrency(charge.baseCost)}</span>
                  </div>

                  {charge.discountPercentage > 0 && (
                    <div className="detail-row discount">
                      <span className="label">Discount ({charge.discountPercentage}%):</span>
                      <span className="value">-{formatCurrency(charge.discountAmount)}</span>
                    </div>
                  )}

                  <div className="detail-row total">
                    <span className="label">Total Due:</span>
                    <span className="value">{formatCurrency(charge.totalDue)}</span>
                  </div>

                  <div className="detail-row due-date">
                    <span className="label">Due Date:</span>
                    <span className="value">{formatDate(charge.dueDate)}</span>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
                  <button
                    className="btn-pay"
                    onClick={() => handlePayNow(charge)}
                    disabled={processingPayment}
                    style={{ flex: 1 }}
                  >
                    {processingPayment ? "Processing..." : "Pay Now"}
                  </button>
                  <button
                    className="btn-download"
                    onClick={() => downloadInvoice(charge)}
                    style={{ flex: 1, background: "#f5f5f5", color: "#333", border: "1px solid #ddd", padding: "10px", borderRadius: "5px", cursor: "pointer" }}
                  >
                    📥 Download Invoice
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Payment History */}
      {activeTab === "history" && (
        <div className="charges-list">
          {paymentHistory.length === 0 ? (
            <div className="empty-state">
              <p>No payment history yet.</p>
            </div>
          ) : (
            paymentHistory.map((charge) => (
              <div key={charge.id} className="charge-card paid">
                <div className="charge-header">
                  <div className="charge-period">
                    <h3>{charge.billingPeriodName}</h3>
                    <span className="charge-frequency">{charge.billingFrequency}</span>
                  </div>
                  <div className="charge-amount">
                    <div className="amount">{formatCurrency(charge.totalDue)}</div>
                    <div className="status paid">✓ PAID</div>
                  </div>
                </div>

                <div className="charge-details">
                  <div className="detail-row">
                    <span className="label">Unit:</span>
                    <span className="value">Floor {charge.floor}, Unit {charge.unit}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Amount Paid:</span>
                    <span className="value">{formatCurrency(charge.paidAmount)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Paid On:</span>
                    <span className="value">{formatDate(charge.paidAt)}</span>
                  </div>
                </div>

                <button
                  className="btn-download"
                  onClick={() => downloadInvoice(charge)}
                  style={{ width: "100%", background: "#f5f5f5", color: "#333", border: "1px solid #ddd", padding: "10px", borderRadius: "5px", cursor: "pointer", marginTop: "15px" }}
                >
                  📥 Download Invoice
                </button>
              </div>
            ))
          )}
        </div>
      )}
      </div>
    </div>
  );
}